import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import { CSSTransition, SwitchTransition } from 'react-transition-group'

import {
  selectFullscreen,
  setSlideshowIsPlaying,
  setVideoIsPlaying,
} from '@store/actionSlices/appConfig'
import { setByFlag, setPackages } from '@store/actionSlices/houseAndLand'
import { setUnitGallery as setLotGallery } from '@store/actionSlices/unitGallery'
import {
  BucketSource,
  LotGallery,
  UnitGalleryData as LotGalleryData,
  ProjectIdentity,
  RootStateTypeExtra,
  SessionMap,
} from '@store/types'

import DataHandler from '@components/data-handler'
import {
  FULFILLED_STATUS,
  PENDING_STATUS,
} from '@components/data-handler/data-handler'
import IdleTimeHandler, {
  useIdleTimeHandler,
} from '@components/idle-time-handler'
import {
  PannellumDataInterface,
  PanoramaType,
} from '@components/showcase-pannellum/types'
import Skeleton from '@components/skeleton'

import {
  selectFromResult as selectFromExternalLinkResult,
  useGetExternalLinksByPropertyQuery,
} from '@api/external-links'
import {
  selectPackagesFromResult,
  useGetPackagesByIdQuery,
} from '@api/houseAndLand'
import {
  ActivePackageConfigurationType,
  FileInterface,
  GalleryInterface,
  HouseAndLandActiveKeyType,
  LotAssetInterface,
  LotInterface,
  MatrixDataInterface,
  PackageInterface,
  PackageOptionsInterface,
} from '@api/types/house-and-land-type'
import {
  selectFromResult as selectFromLotGalleryResult,
  useGetUnitGalleryQuery as useGetLotGalleryQuery,
} from '@api/unit-gallery'

import { FileType } from '@utilities/adgroup-utilities/constants/common'
import { HouseAndLandPackageOptionType } from '@utilities/adgroup-utilities/types/houseAndLand'
import useEnableCarouselKeyboardControl from '@utilities/useEnableCarouselKeyboardControl'
import VideoPlayerFirebaseService from '@utilities/video-player-firebase-service'
import mapWhiteLabelsWithDefaults from '@utilities/white-label'

import { ArrowSvg, LinkSvg } from '@svg/react'

import AssetHandler from '@adUtilities/asset-handler/asset-handler'
import { CarouselProvider } from '@adUtilities/components/carousel-provider'
import { useCompareProperty } from '@adUtilities/components/compare-property/utils'
import VideoPlayer from '@adUtilities/components/video-player'
import { VideoPlayerRef } from '@adUtilities/components/video-player/video-player'
import { CarouselControlType } from '@adUtilities/constants/carousel'
import {
  BufferState,
  EndState,
  PlayState,
  SeekState,
  SyncState,
} from '@adUtilities/constants/player'
import mapPropertyGallery, {
  FloorImage,
  PropertyGallery,
} from '@adUtilities/property-gallery'
import { ViewLineImage } from '@adUtilities/types'

import Controls from './controls'
import DeselectedView from './deselected-view'
import LotViewSkeleton from './lot-view-skeleton'
import PackageInfoCard from './package-info-card'

interface LastActiveOption {
  type: string
  id: string
}

export interface LotViewProps {
  lot: LotInterface
  packageInfoToggle: boolean
  setPackageInfoToggle: (status: boolean) => void
  activePackage: PackageInterface | undefined
  packages: Array<PackageOptionsInterface>
  activePrecinctId: string
  activeStageId: string
  projectIdentity: ProjectIdentity
  session: SessionMap | undefined
  panoramic: Array<PannellumDataInterface>
  lotGalleryControlStyle: string
  lastActiveOption: LastActiveOption
  activePackageConfiguration: ActivePackageConfigurationType | undefined
  lotGalleries: Array<LotGalleryData>
  setFullScreenToggle: (arg: boolean) => void
  handlePackageInfoModal: () => void
  onlyShowLotGalleries: boolean
  activeMatrixData: MatrixDataInterface | undefined
  getVideoPlayerState: (arg: boolean) => void
  appIdleControls: {
    active: boolean
    duration: number
  }
  effectType: string
  autoPlayIntervalInSeconds: number
  comparePropertyProps: ReturnType<typeof useCompareProperty>
  panoramicButtonTitle: string
}

const noImageObject = {
  id: 'no-image',
  src: '',
  label: 'No Image',
  type: FileType.Image,
  noSpliceUrl: false,
}

function LotView({
  lot,
  packageInfoToggle,
  setPackageInfoToggle,
  activePackage,
  packages,
  activePrecinctId,
  activeStageId,
  projectIdentity,
  session,
  panoramic,
  lotGalleryControlStyle,
  lastActiveOption,
  activePackageConfiguration,
  setFullScreenToggle,
  lotGalleries,
  handlePackageInfoModal,
  onlyShowLotGalleries,
  activeMatrixData,
  getVideoPlayerState,
  appIdleControls,
  effectType,
  autoPlayIntervalInSeconds,
  comparePropertyProps,
  panoramicButtonTitle,
}: LotViewProps) {
  const fullscreen = useSelector(selectFullscreen)
  const { projectId, projectName } = projectIdentity
  const dispatch = useDispatch()

  const [isConnected, setIsConnected] = useState(false)
  const [shouldShowSlideshowBtn, setShouldShowSlideShowBtn] = useState(false)
  const [deselected, setDeselectState] = useState(false)
  const [showExternalLinks, setShowExternalLinks] = useState(false)
  const [iframeLoaded, setIframeLoaded] = useState(false)
  const [activeLabel, setActiveLabel] = useState('')

  const isAppIdle = useIdleTimeHandler({
    isConnected,
    timeoutInSeconds: appIdleControls?.duration || 3,
    isIdleActive: appIdleControls?.active || false,
  })

  const activePackageData: PackageOptionsInterface | undefined = useMemo(() => {
    if (packages.length === 0 || !activePackage) {
      return undefined
    }

    return packages.find(
      (item: PackageOptionsInterface) =>
        item.id === activePackage.id || item.name === activePackage.id
    )
  }, [packages, activePackage])

  const packagePayload = useGetPackagesByIdQuery(
    { projectName, lotId: lot.id },
    {
      selectFromResult: selectPackagesFromResult,
    }
  )

  const lotGalleryPayload = useGetLotGalleryQuery(
    { projectName, unitId: lot.name },
    {
      selectFromResult: selectFromLotGalleryResult,
      skip: packagePayload.status !== FULFILLED_STATUS,
    }
  )

  const getPackageOptionDetailById = useCallback(
    (
      type: HouseAndLandPackageOptionType,
      id: string
    ): { images: FileInterface[]; price: number } => {
      if (!activePackageData) {
        return {
          images: [],
          price: 0,
        }
      }

      const {
        FloorplanOption,
        Facade,
        FacadeColor,
        InternalTheme,
        InternalColorScheme,
      } = HouseAndLandPackageOptionType

      if (type === FloorplanOption) {
        const optionData = activePackageData[type]?.find(
          (item) => item.id === id
        )
        return {
          images: optionData?.images || [],
          price: optionData?.price || 0,
        }
      }

      if (type === Facade) {
        const optionData = activePackageData[type]?.find(
          (item) => item.id === id
        )
        return {
          images: optionData?.images || [],
          price: optionData?.price || 0,
        }
      }

      if (type === FacadeColor) {
        let images: FileInterface[] = []
        let price = 0
        activePackageData[Facade]?.forEach((item) => {
          item.facadeColors.forEach((facadeColor) => {
            if (facadeColor.id === id) {
              images = images.concat(facadeColor.images)
              price = facadeColor.price || 0
            }
          })
        })

        return { images, price }
      }

      if (type === InternalTheme) {
        const optionData = activePackageData[type]?.find(
          (item) => item.id === id
        )
        return {
          images: optionData?.images || [],
          price: optionData?.price || 0,
        }
      }

      if (type === InternalColorScheme) {
        let images: FileInterface[] = []
        let price = 0
        activePackageData[InternalTheme]?.forEach((item) => {
          item.internalColorSchemes.forEach((facadeColor) => {
            if (facadeColor.id === id) {
              images = images.concat(facadeColor.images)
              price = facadeColor.price || 0
            }
          })
        })
        return { images, price }
      }

      return { images: [], price: 0 }
    },
    [activePackageData]
  )

  const transformImageAsset = (item: LotAssetInterface) => ({
    id: item.id,
    src: item.url,
    label: item.title || item.name,
    order: item.order,
  })

  const transformDataForGallery = (
    argData: Array<
      (FileInterface | GalleryInterface) & { noSpliceUrl?: boolean }
    >
  ): LotGallery[] =>
    argData
      .sort((a, b) => a.order - b.order)
      .map((item) => ({
        id: item.id,
        src: item.url,
        label: item.title || item.name,
        type: 'type' in item ? (item.type as FileType) : FileType.Image,
        thumbnail: 'thumbnail' in item ? (item.thumbnail as string) : '',
        noSpliceUrl: Boolean(item.noSpliceUrl),
      }))

  const galleryData: LotGallery[] = useMemo(() => {
    setDeselectState(false)

    if (!activePackageData) {
      const floorItems: FloorImage[] = lot?.assets?.floorImage
        ? [transformImageAsset(lot.assets.floorImage)]
        : []

      const viewLineItems: ViewLineImage[] =
        lot?.assets?.viewLinesImage?.map(transformImageAsset) || []

      const myLotGalleries =
        lotGalleries.find((item) => item.unit === lot.name)?.items || []

      const propertyGalleries: PropertyGallery[] = myLotGalleries.map(
        (item) => ({
          id: item.id,
          src: item.src,
          label: item.label || '',
          type: item.type,
          thumbnail: item.thumbnail,
          noSpliceUrl: false,
        })
      )

      return mapPropertyGallery({
        projectId,
        floorImages: floorItems,
        viewLineImages: viewLineItems,
        propertyGalleries,
        onlyShowPropertyGallery: onlyShowLotGalleries,
      })
    }

    if (
      Object.values(HouseAndLandPackageOptionType).includes(
        lastActiveOption.type as HouseAndLandPackageOptionType
      )
    ) {
      if (!lastActiveOption.id) {
        setDeselectState(true)
        setActiveLabel('')
        return []
      }

      const myImages: FileInterface[] = getPackageOptionDetailById(
        lastActiveOption.type as HouseAndLandPackageOptionType,
        lastActiveOption.id
      ).images

      if (myImages.length === 0) {
        return [noImageObject]
      }

      return transformDataForGallery([...myImages])
    }

    const packageGalleries = activePackageData?.galleries || []

    if (packageGalleries.length === 0) {
      return [noImageObject]
    }

    return transformDataForGallery([...packageGalleries])
  }, [
    activePackageData,
    lastActiveOption,
    lotGalleries,
    onlyShowLotGalleries,
    lot?.id,
    projectId,
  ])

  const totalPrice = useMemo((): number => {
    const { price: lotPrice } = lot

    let totalPrice = lotPrice

    if (!activePackageData) {
      return totalPrice
    }

    if (activePackageData.housePrice) {
      totalPrice += activePackageData.housePrice
    }

    if (!activePackageConfiguration) {
      return totalPrice
    }

    const {
      FloorplanOption,
      Facade,
      FacadeColor,
      InternalTheme,
      InternalColorScheme,
    } = HouseAndLandPackageOptionType

    if (activePackageConfiguration[FloorplanOption]) {
      totalPrice +=
        getPackageOptionDetailById(
          FloorplanOption,
          activePackageConfiguration[FloorplanOption]
        ).price || 0
    }

    if (activePackageConfiguration[Facade]) {
      totalPrice +=
        getPackageOptionDetailById(Facade, activePackageConfiguration[Facade])
          .price || 0
    }

    if (activePackageConfiguration[FacadeColor]) {
      totalPrice +=
        getPackageOptionDetailById(
          FacadeColor,
          activePackageConfiguration[FacadeColor]
        ).price || 0
    }

    if (activePackageConfiguration[InternalTheme]) {
      totalPrice +=
        getPackageOptionDetailById(
          InternalTheme,
          activePackageConfiguration[InternalTheme]
        ).price || 0
    }

    if (activePackageConfiguration[InternalColorScheme]) {
      totalPrice +=
        getPackageOptionDetailById(
          InternalColorScheme,
          activePackageConfiguration[InternalColorScheme]
        ).price || 0
    }

    return totalPrice
  }, [lot, activePackageData, activePackageConfiguration])

  const hasPanoramicData: boolean = useMemo(() => {
    const foundData = panoramic.find(
      (item: PannellumDataInterface) =>
        item.type === PanoramaType.Lot &&
        item.panoramaGroup?.toString() === lot.name?.toString()
    )
    return foundData !== undefined
  }, [panoramic, lot])

  const getFilteredPackages = useCallback(
    (
      apiPackages: Array<PackageOptionsInterface>
    ): Array<PackageOptionsInterface> =>
      apiPackages.filter(
        (pItem: PackageOptionsInterface) =>
          !packages.find(
            (item: PackageOptionsInterface) => item.id === pItem.id
          )
      ),
    [packages]
  )

  const externalLinksPayload = useGetExternalLinksByPropertyQuery(
    { projectName, propertyId: lot?.id || '' },
    {
      skip: !lot?.id,
      selectFromResult: selectFromExternalLinkResult,
    }
  )

  const videoPlayerRef = useRef<VideoPlayerRef>(null)

  const videoPlayerFirebaseService = VideoPlayerFirebaseService({
    galleryName: 'propertyGallery',
    projectIdentity,
  })

  const [activeSlideIndex, setActiveSlideIndex] = useState(0)
  const [autoPlayState, setAutoPlayState] = useState(false)
  const [playerState, setPlayerState] = useState(false)
  const [videoSyncKey, setVideoSyncKey] = useState<number | null>(null)

  const enableKeyboardControl = useEnableCarouselKeyboardControl({
    isSessionConnected: isConnected,
    playerState,
  })

  const slides = useMemo(
    () =>
      (galleryData || []).map((item) => ({
        id: item.id,
        label: item.label,
        src:
          item.type === FileType.Image && item.src
            ? item.src
            : item.thumbnail || '',
        thumbnail: item.thumbnail,
        originalSrc: item.src,
        bucketType: BucketSource.Legacy,
        fileType: item.type as FileType,
        noSpliceUrl: item.noSpliceUrl,
      })),
    [galleryData]
  )
  const activeSlide = useMemo(
    () => slides[activeSlideIndex],
    [slides, activeSlideIndex]
  )

  const hasVideoItems = useMemo(
    () => slides.some((item) => item.fileType === FileType.Video),
    [slides]
  )

  const videoSource = useMemo(
    () =>
      (activeSlide?.fileType === FileType.Video &&
        AssetHandler({
          url: activeSlide.originalSrc,
          type: activeSlide.bucketType,
          fileType: FileType.Video,
        })) ||
      '',
    [slides, activeSlide]
  )

  const setSlideShowToggle = (status: boolean) => {
    setAutoPlayState((prevVal) => !prevVal)
    if (!status) {
      setActiveSlideIndex(0)
    }
    setFullScreenToggle(status)
    dispatch(setSlideshowIsPlaying(status))
  }

  const handleIframLoad = () => {
    setIframeLoaded(true)
  }

  const { compareModeIsActive } = comparePropertyProps || {}
  const hideCompareAction = useMemo(() => {
    if (activePackage) {
      return true
    }
    if (galleryData.length === 1) {
      return false
    }
    if (!(activeSlideIndex === 0 && galleryData?.[activeSlideIndex])) {
      return true
    }
    return false
  }, [activePackage, activeSlideIndex, galleryData])

  const handlePlayerState = useCallback(
    (state: boolean) => {
      getVideoPlayerState(state)
      setPlayerState(state)
      dispatch(setVideoIsPlaying(state))
    },
    [getVideoPlayerState]
  )

  useEffect(
    () => () => {
      dispatch(setSlideshowIsPlaying(false))
      dispatch(setVideoIsPlaying(false))
    },
    []
  )

  useEffect(() => {
    const { packages: packageData } = packagePayload
    const filteredPackages: Array<PackageOptionsInterface> =
      getFilteredPackages(packageData)
    if (packageData.length > 0 && filteredPackages.length > 0) {
      dispatch(setPackages(filteredPackages))
    }
  }, [packagePayload])

  useEffect(() => {
    const {
      unitGalleryData: lotGalleryData,
      isLoaded,
      isError,
    } = lotGalleryPayload
    if (
      isLoaded &&
      lotGalleryData.length > 0 &&
      !lotGalleries.find((item) => item.unit === lot.name) &&
      !isError
    ) {
      dispatch(setLotGallery({ unit: lot.name, items: lotGalleryData }))
    }
  }, [lotGalleryPayload])

  useEffect(() => {
    if (lot.name) {
      dispatch(
        setByFlag({
          flag: HouseAndLandActiveKeyType.ActiveLotName,
          value: lot.name,
        })
      )
    }
  }, [lot])

  useEffect(() => {
    if (session) {
      const {
        connected,
        houseAndLand: { lotInfoPopup, externalLinkPopup },
      } = session
      setIsConnected(connected)
      setPackageInfoToggle(lotInfoPopup)
      setShowExternalLinks(externalLinkPopup)
    }
  }, [session?.connected, session?.houseAndLand, session?.propertyGallery])

  useEffect(() => {
    setShouldShowSlideShowBtn(galleryData.length > 1)
  }, [galleryData])

  useEffect(() => {
    if (!session) {
      return
    }
    const {
      connected,
      propertyGallery: {
        galleryControlV2: {
          activeSlideIndex: activeSlideIndexFirebase,
          autoPlayState: autoPlayStateFirebase,
        },
        playerControlV2: {
          playerState: playerStateFirebase,
          loop: loopFirebase,
          muted: mutedFirebase,
          volume: volumeFirebase,
          seekToValueInSeconds: seekToValueInSecondsFirebase,
          videoSyncKey: videoSyncKeyFirebase,
        },
      },
    } = session

    setIsConnected(connected)
    setAutoPlayState(autoPlayStateFirebase)
    setActiveSlideIndex(activeSlideIndexFirebase)

    videoPlayerRef.current?.updatePlayerState(playerStateFirebase)
    videoPlayerRef.current?.toggleLoop(loopFirebase)
    videoPlayerRef.current?.toggleMute(mutedFirebase)
    videoPlayerRef.current?.updateVolume(volumeFirebase / 10)
    if (seekToValueInSecondsFirebase !== null) {
      videoPlayerRef.current?.seekTo(seekToValueInSecondsFirebase)
    }
    if (videoSyncKeyFirebase !== null) {
      setVideoSyncKey(videoSyncKeyFirebase)
    }
  }, [session?.connected, session?.propertyGallery])

  const handleOnPlay = async (args: PlayState) => {
    await videoPlayerFirebaseService.updateStateOnPlay(args)
  }
  const handleOnBuffer = async (args: BufferState) => {
    await videoPlayerFirebaseService.updateStateOnBuffer(args)
  }
  const handleOnSeek = async (args: SeekState) => {
    await videoPlayerFirebaseService.updateStateOnSeek(args)
  }
  const handleOnSync = async (args: SyncState) => {
    await videoPlayerFirebaseService.updateStateOnSyncRequest(args)
  }
  const handleOnEnd = async (args: EndState) => {
    await videoPlayerFirebaseService.updateStateOnEnd(args)
  }

  const dataHandlerPayload = useMemo(() => {
    if (packagePayload.status === FULFILLED_STATUS) {
      const packageCountOfThisLot = activeMatrixData
        ? activeMatrixData?.packages?.filter((item: PackageInterface) =>
            item.lots?.includes(lot.id)
          )?.length
        : 0

      let data
      if (packageCountOfThisLot > 0) {
        data = packages
      } else if (lotGalleryPayload?.unitGalleryData?.length > 0) {
        data = lotGalleryPayload.unitGalleryData
      } else {
        data = lot?.id
      }

      return {
        ...lotGalleryPayload,
        status:
          lotGalleryPayload.unitGalleryData?.length > 0
            ? (lotGalleries.length && FULFILLED_STATUS) || PENDING_STATUS
            : lotGalleryPayload.status,
        data,
      }
    }

    return {
      ...packagePayload,
      data: packages,
    }
  }, [
    lotGalleryPayload,
    packagePayload,
    packages,
    activeMatrixData,
    lot?.id,
    lotGalleries?.length,
  ])

  return (
    <DataHandler
      payloads={{
        ...dataHandlerPayload,
        status:
          isConnected && !session?.houseAndLand?.isDataLoaded
            ? PENDING_STATUS
            : dataHandlerPayload.status,
      }}
      skeletonFrame={<LotViewSkeleton />}
    >
      <div className="relative flex h-full w-full bg-gray-700/50">
        <IdleTimeHandler disable={isConnected}>
          {!isConnected && (
            <div className="absolute right-8 top-7 z-10">
              <Controls
                lot={lot}
                hasPanoramicData={hasPanoramicData}
                setFullScreenToggle={setFullScreenToggle}
                packageInfoToggle={packageInfoToggle}
                slideShowToggle={autoPlayState}
                shouldShowSlideshowBtn={shouldShowSlideshowBtn}
                setSlideShowToggle={setSlideShowToggle}
                setPackageInfoToggle={setPackageInfoToggle}
                isVideoPlaying={playerState}
                comparePropertyProps={comparePropertyProps}
                hideCompareAction={hideCompareAction}
                panoramicButtonTitle={panoramicButtonTitle}
              />

              {!autoPlayState &&
                externalLinksPayload?.externalLinks?.length > 0 && (
                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      className="lot-view-control-icon cursor-pointer"
                      onClick={() => setShowExternalLinks(!showExternalLinks)}
                    >
                      <LinkSvg className="h-5 w-5" />
                    </button>
                  </div>
                )}
            </div>
          )}
          {compareModeIsActive ? null : (
            <div
              className={`pointer-events-none absolute bottom-0 z-10 h-auto w-full bg-shadow-horizontal transition duration-300 ease-in ${
                packageInfoToggle && !autoPlayState && !playerState
                  ? 'translate-y-0'
                  : 'translate-y-full'
              }`}
            >
              <div
                className={`flex h-full transform items-stretch justify-between duration-200 ease-in ${
                  fullscreen ? 'pb-2 pl-2' : 'pb-5 pl-5'
                } `}
              >
                <PackageInfoCard
                  lot={lot}
                  packageInfo={activePackage}
                  totalPrice={totalPrice}
                  handlePackageInfoModal={handlePackageInfoModal}
                  connected={isConnected}
                  activePrecinctId={activePrecinctId}
                  activeStageId={activeStageId}
                />

                {isConnected && (
                  <div className="self-end px-16 text-[22px] font-medium leading-5 text-white drop-shadow-40">
                    {activeLabel}
                  </div>
                )}
              </div>
            </div>
          )}
        </IdleTimeHandler>
        <SwitchTransition>
          <CSSTransition
            timeout={200}
            classNames="transition-fade-out"
            key={deselected ? 'deselected' : galleryData?.[0]?.id}
          >
            {deselected ? (
              <DeselectedView type={lastActiveOption.type} />
            ) : (
              <div className="absolute inset-0">
                <CarouselProvider
                  gallerySettings={{ effectType, autoPlayIntervalInSeconds }}
                  slides={slides}
                  activeSlideIndex={activeSlideIndex}
                  showControl={!isConnected && !autoPlayState}
                  controlType={lotGalleryControlStyle as CarouselControlType}
                  mousewheel={!isConnected && !autoPlayState}
                  getCurrentSlideIndex={setActiveSlideIndex}
                  autoPlay={autoPlayState}
                  controlPosition="right"
                  isAppIdle={isAppIdle}
                  enableKeyboardControl={enableKeyboardControl}
                />
              </div>
            )}
          </CSSTransition>
        </SwitchTransition>
        {hasVideoItems && !compareModeIsActive ? (
          <div
            className={`absolute inset-0 ${
              videoSource ? 'visible' : 'invisible'
            }`}
          >
            <div className="flex h-full w-full items-center justify-center">
              <VideoPlayer
                key={`lot-gallery-video-player-${isConnected.toString()}`}
                ref={videoPlayerRef}
                sources={[videoSource]}
                activeIndex={0}
                disablePlayButton={isConnected || autoPlayState}
                showLoopOption={false}
                videoSyncKey={videoSyncKey}
                showControls={!isConnected}
                getPlayerState={handlePlayerState}
                {...(isConnected && {
                  updateStateOnPlay: handleOnPlay,
                  updateStateOnBuffer: handleOnBuffer,
                  updateStateOnSeek: handleOnSeek,
                  updateStateOnSyncRequest: handleOnSync,
                  updateStateOnEnd: handleOnEnd,
                })}
                hidePlayButton={isConnected}
              />
            </div>
          </div>
        ) : null}
      </div>

      {showExternalLinks && !compareModeIsActive && (
        <div
          className={`fixed left-0 z-30 w-screen bg-secondaryColour ${
            isConnected || fullscreen
              ? 'top-0 h-screen'
              : 'top-9 h-page-container overflow-hidden rounded-t-3xl'
          }`}
        >
          {!isConnected && (
            <button
              type="button"
              className="absolute left-5 top-5 z-3 rounded bg-white p-1 drop-shadow-40"
              onClick={() => {
                setShowExternalLinks(false)
                setIframeLoaded(false)
              }}
            >
              <ArrowSvg className="h-8 w-8" strokeColor="#000" />
            </button>
          )}

          {!iframeLoaded && <Skeleton />}

          <iframe
            key={externalLinksPayload?.externalLinks?.[0]?.linkUrl}
            className={`relative z-2 h-full w-full overflow-hidden border-0 ${
              !iframeLoaded ? 'opacity-0' : ''
            }`}
            title={externalLinksPayload?.externalLinks?.[0]?.label}
            src={externalLinksPayload?.externalLinks?.[0]?.linkUrl}
            height="100%"
            width="100%"
            onLoad={handleIframLoad}
          />
        </div>
      )}
    </DataHandler>
  )
}

export default connect(
  ({
    projectIdentity,
    houseAndLand: { packages, activePrecinctId, activeStageId },
    firestore: { session },
    panoramic,
    projectConfig: {
      propertyGalleryControlStyle: lotGalleryControlStyle,
      onlyShowPropertyGalleries,
      propertyGalleryTab,
      gallerySettings: { type, intervalInSeconds },
    },
    unitGallery,
    whiteLabel,
  }: RootStateTypeExtra) => ({
    projectIdentity,
    packages,
    activePrecinctId,
    activeStageId,
    session,
    panoramic,
    lotGalleryControlStyle,
    onlyShowLotGalleries: onlyShowPropertyGalleries,
    lotGalleries: unitGallery,
    appIdleControls: propertyGalleryTab,
    effectType: type,
    autoPlayIntervalInSeconds: intervalInSeconds,
    panoramicButtonTitle:
      mapWhiteLabelsWithDefaults(whiteLabel).propertyView.panoramicButtonTitle,
  })
)(LotView)
